import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import CleanupLink from './CleanupLink';

gsap.registerPlugin(ScrollTrigger);

// ─────────────────────────────────────────────────────────────────
// SCROLL FREEZE BUG — ROOT CAUSE & FIX
//
// When scrolling back up fast through BannerTwo the page would
// freeze for 2–3 seconds before snapping to BannerSection.
//
// Root cause: the .btwo-stack wrapper had NO explicit height.
// CSS sticky positioning requires the parent to have a known height
// so the browser knows when sticky elements should "unstick".
// Without it, the browser recalculates scroll geometry on every
// scroll event. Combined with GSAP ScrollTrigger also recalculating
// on scroll, and momentum/inertia scrolling overshooting the
// boundary, the compositor gets into a deadlock: scroll position
// is past the sticky boundary but layout hasn't resolved yet.
//
// FIX 1: Give .btwo-stack an explicit height of 400vh
//   (4 cards × 100vh each). The browser now knows exactly when to
//   unstick and doesn't need to recalculate on every scroll frame.
//
// FIX 2: Add `overflow-anchor: none` to .btwo-stack.
//   Scroll anchoring is a browser feature that adjusts scroll
//   position to keep content stable when DOM changes above the
//   viewport. When sticky elements change stacking order rapidly
//   (during fast scroll), scroll anchoring can fight the scroll
//   position, causing the freeze. Disabling it removes this conflict.
//
// FIX 3: ScrollTrigger.config({ ignoreMobileResize: true }) and
//   refreshing on window load rather than relying on auto-refresh
//   prevents GSAP from triggering layout thrash during fast scroll.
// ─────────────────────────────────────────────────────────────────

const slideData = [
  {
    className: 'banner-two-1',
    title: 'Embroidery Digitizing',
    video: '/images/embroideryvideo.mp4',
    link: '/contact',
    description:
      'We deliver precise, high-quality embroidery digitizing optimized for flawless machine stitching. Every design is carefully crafted to ensure clean details.',
  },
  {
    className: 'banner-two-2',
    title: 'Custom Patch Digitizing',
    video: '/images/patchvideo.mp4',
    link: '/custom-patches-pricing',
    description:
      'We specialize in custom patch digitizing that brings your designs to life with bold outlines, rich fill stitches, and professional-grade precision for every patch type.',
  },
  {
    className: 'banner-two-3',
    title: 'Web Design & Development',
    video: '/images/webdev.mp4',
    link: '/contact',
    description:
      'We build modern, high-performing websites that combine stunning design with seamless functionality.',
  },
  {
    className: 'banner-two-4',
    title: 'Branding Design',
    video: '/images/branding.mp4',
    link: '/contact',
    description:
      'We craft powerful brand identities that leave lasting impressions and set you apart from the competition.',
  },
];

const VideoSlot = ({ slide, isVisible }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isVisible) {
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      video.pause();
    }
  }, [isVisible]);

  return (
    <video
      ref={videoRef}
      src={slide.video}
      muted
      loop
      playsInline
      preload="auto"
      style={{ width: '100%', height: '450px', objectFit: 'cover', borderRadius: '10px' }}
    />
  );
};

const BannerTwo = () => {
  const cardRefs = useRef([]);
  const [visibleIndex, setVisibleIndex] = useState(null);

  useEffect(() => {
    // Prevent GSAP ScrollTrigger from recalculating on every mobile resize event
    // (address-bar show/hide), which can interfere with sticky scroll geometry.
    ScrollTrigger.config({ ignoreMobileResize: true });

    const mm = gsap.matchMedia();
    const ctx = gsap.context(() => {
      mm.add('(min-width: 501px)', () => {
        cardRefs.current.forEach((card, i) => {
          if (i === slideData.length - 1) return;
          const nextCard = cardRefs.current[i + 1];
          if (!card || !nextCard) return;

          gsap.fromTo(
            card,
            { scale: 1, autoAlpha: 1, y: 0 },
            {
              scale: 0.96,
              autoAlpha: 0.35,
              y: -10,
              ease: 'none',
              scrollTrigger: {
                trigger: nextCard,
                start: 'top 90%',
                end: 'top 15%',
                scrub: 0.6,
              },
            }
          );
        });
      });
    });

    // Force a single clean ScrollTrigger refresh after everything is mounted
    // rather than letting it auto-refresh during scroll events.
    const onLoad = () => ScrollTrigger.refresh();
    window.addEventListener('load', onLoad);

    return () => {
      ctx.revert();
      mm.revert();
      window.removeEventListener('load', onLoad);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const idx = parseInt(entry.target.dataset.index, 10);
          if (entry.isIntersecting) {
            setVisibleIndex(idx);
          } else {
            setVisibleIndex(prev => (prev === idx ? null : prev));
          }
        });
      },
      { threshold: 0.6 }
    );

    cardRefs.current.forEach((card, i) => {
      if (card) {
        card.dataset.index = i;
        observer.observe(card);
      }
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        .btwo-stack {
          /* CRITICAL: explicit height so the browser knows the sticky boundary.
             Without this, sticky geometry is recalculated on every scroll frame,
             causing the freeze when scrolling back up fast.
             4 cards × 100vh = 400vh total scroll distance. */
          height: 400vh;
          /* overflow-anchor: none prevents the browser's scroll anchoring
             from fighting the scroll position when sticky elements change
             stacking order rapidly during fast upward scrolling. */
          overflow-anchor: none;
        }

        .btwo-sticky-slot {
          height: 100vh;
          display: flex;
          align-items: center;
          position: sticky;
          top: 0;
        }
        .btwo-sticky-slot:nth-child(1) { z-index: 1; }
        .btwo-sticky-slot:nth-child(2) { z-index: 2; }
        .btwo-sticky-slot:nth-child(3) { z-index: 3; }
        .btwo-sticky-slot:nth-child(4) { z-index: 4; }

        .btwo-card {
          width: 100%;
          transform-origin: center center;
        }

        .btwo-stack .banner-two {
          padding: 0 !important;
          background: transparent !important;
        }
      `}</style>

      <div className="btwo-stack">
        {slideData.map((slide, i) => (
          <div className="btwo-sticky-slot" key={i}>
            <div
              className="btwo-card banner-two"
              ref={el => cardRefs.current[i] = el}
            >
              <div className="container">
                <div className={`banner-two-slider ${slide.className}`}>
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <div className="banner-two-content">
                        <h1 className="text-white">
                          {slide.title} <span>Experts</span>
                        </h1>
                        <p className="p-text text-white">{slide.description}</p>
                        <CleanupLink className="btn btn-primary" to={slide.link}>
                          Start Your Project
                        </CleanupLink>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <VideoSlot slide={slide} isVisible={visibleIndex === i} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default BannerTwo;